import {createContext, useCallback, useContext, useState, type ReactNode} from 'react';
import {AwButton, AwField, AwModal} from '@ascension-tracker/ascension-ui';

type ConfirmOptions = {title?: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean};
type AlertOptions = {title?: string; okLabel?: string};
type TypedConfirmOptions = ConfirmOptions & {fieldLabel?: string};

type DialogState =
  | {kind: 'alert'; message: ReactNode; options: AlertOptions; resolve: () => void}
  | {kind: 'confirm'; message: ReactNode; options: ConfirmOptions; resolve: (value: boolean) => void}
  | {kind: 'typed-confirm'; message: ReactNode; requiredText: string; options: TypedConfirmOptions; resolve: (value: boolean) => void};

type DialogContextValue = {
  alert: (message: ReactNode, options?: AlertOptions) => Promise<void>;
  confirm: (message: ReactNode, options?: ConfirmOptions) => Promise<boolean>;
  confirmTyped: (message: ReactNode, requiredText: string, options?: TypedConfirmOptions) => Promise<boolean>;
};

const DialogContext = createContext<DialogContextValue | null>(null);

export function useDialogs() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialogs must be used within a DialogProvider');
  return ctx;
}

export function DialogProvider({children}: {children: ReactNode}) {
  const [state, setState] = useState<DialogState | null>(null);
  const [typedValue, setTypedValue] = useState('');

  const alert = useCallback((message: ReactNode, options: AlertOptions = {}) =>
    new Promise<void>(resolve => setState({kind: 'alert', message, options, resolve})), []);

  const confirm = useCallback((message: ReactNode, options: ConfirmOptions = {}) =>
    new Promise<boolean>(resolve => setState({kind: 'confirm', message, options, resolve})), []);

  const confirmTyped = useCallback((message: ReactNode, requiredText: string, options: TypedConfirmOptions = {}) =>
    new Promise<boolean>(resolve => { setTypedValue(''); setState({kind: 'typed-confirm', message, requiredText, options, resolve}); }), []);

  function settle(value: boolean) {
    if (!state) return;
    if (state.kind === 'alert') state.resolve(); else state.resolve(value);
    setState(null);
  }

  const title = state?.options.title ?? (state?.kind === 'alert' ? 'Notice' : 'Confirm');

  return (
    <DialogContext.Provider value={{alert, confirm, confirmTyped}}>
      {children}
      <AwModal open={Boolean(state)} title={title} onClose={() => settle(false)}>
        {state && <div className="aw-dialog">
          <div className="aw-dialog__message">{state.message}</div>
          {state.kind === 'typed-confirm' && (
            <AwField
              id="dialog-typed-confirm-input"
              label={state.options.fieldLabel ?? `Type "${state.requiredText}" to confirm`}
              value={typedValue}
              onChange={event => setTypedValue(event.target.value)}
              autoFocus
              autoComplete="off"
            />
          )}
          <div className="project-form__actions">
            {state.kind === 'alert'
              ? <AwButton type="button" onClick={() => settle(false)}>{state.options.okLabel ?? 'OK'}</AwButton>
              : <>
                  <AwButton type="button" variant="secondary" onClick={() => settle(false)}>{state.options.cancelLabel ?? 'Cancel'}</AwButton>
                  <AwButton
                    type="button"
                    variant={state.options.danger ? 'danger' : 'primary'}
                    disabled={state.kind === 'typed-confirm' && typedValue !== state.requiredText}
                    onClick={() => settle(true)}
                  >
                    {state.options.confirmLabel ?? 'Confirm'}
                  </AwButton>
                </>}
          </div>
        </div>}
      </AwModal>
    </DialogContext.Provider>
  );
}
